import React, { useState } from "react";
import axios from "axios";
import { Send, MapPin, Phone, Clock, MessageCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { company } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { name: "", email: "", phone: "", message: "" };

const useContactSubmit = (source) => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast({ title: "Email required", description: "Please provide your email address.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, source });
      setForm(EMPTY_FORM);
      toast({
        title: "Message sent!",
        description: "Thanks for reaching out \u2014 we'll be in touch shortly.",
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "We couldn't send your message. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return { form, submitting, handleChange, handleSubmit };
};

const ContactFormCard = ({ compact, source }) => {
  const { form, submitting, handleChange, handleSubmit } = useContactSubmit(source);

  return (
    <div className="rounded-2xl bg-white p-7 sm:p-9 shadow-xl shadow-slate-200/60 border border-slate-100">
      <h3 className="font-display text-2xl font-bold text-slate-900">Drop us a line!</h3>
      <p className="mt-2 text-sm text-slate-500">Tell us about your project and we'll get back to you.</p>
      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">Name</label>
          <Input name="name" value={form.name} onChange={handleChange} placeholder="Your name" className="mt-1.5 rounded-lg" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Email <span className="text-primary">*</span></label>
          <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@company.com" className="mt-1.5 rounded-lg" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Phone</label>
          <Input name="phone" value={form.phone} onChange={handleChange} placeholder="04xx xxx xxx" className="mt-1.5 rounded-lg" />
        </div>
        {!compact && (
          <div>
            <label className="text-sm font-medium text-slate-700">Message</label>
            <Textarea name="message" value={form.message} onChange={handleChange} placeholder="How can we help?" rows={4} className="mt-1.5 rounded-lg resize-none" />
          </div>
        )}
        <Button type="submit" disabled={submitting} className="w-full rounded-lg bg-primary hover:bg-primary/90 shadow-md shadow-primary/20">
          {submitting ? "Sending..." : (<><Send className="h-4 w-4 mr-2" /> Send Message</>)}
        </Button>
        <p className="text-xs text-slate-400 leading-relaxed">
          This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
        </p>
      </form>
    </div>
  );
};

const ContactInfoCard = () => (
  <div className="space-y-5">
    <div className="rounded-2xl bg-slate-900 text-white p-7 sm:p-9">
      <h3 className="font-display text-2xl font-bold">Better yet, see us in person!</h3>
      <p className="mt-2 text-slate-300 text-sm leading-relaxed">
        We love our customers, so feel free to visit during normal business hours.
      </p>
      <a
        href={company.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
      >
        <MessageCircle className="h-4 w-4" /> Message us on WhatsApp
      </a>
      <div className="mt-7 space-y-4 text-sm">
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-primary shrink-0" />
          <span className="text-slate-300">{company.location}</span>
        </div>
        <div className="flex items-center gap-3">
          <Phone className="h-5 w-5 text-primary shrink-0" />
          <a href={`tel:${company.phoneRaw}`} className="text-slate-300 hover:text-white">{company.phone}</a>
        </div>
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 text-primary shrink-0" />
          <span className="text-slate-300">Open today: {company.hoursToday}</span>
        </div>
      </div>
    </div>
    <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm h-56">
      <iframe
        title="OzzieeTech location"
        src="https://www.google.com/maps?q=Australia&output=embed"
        className="w-full h-full border-0"
        loading="lazy"
      />
    </div>
  </div>
);

const ContactForm = ({ compact = false, source = "website" }) => (
  <div className="grid gap-8 lg:grid-cols-2 items-start">
    <ContactFormCard compact={compact} source={source} />
    <ContactInfoCard />
  </div>
);

export default ContactForm;
